import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Send, Building, Clock, MessageCircle } from 'lucide-react';
import clientAxios from '../api/clientAxios';
import loadingGIF from '../assets/images/productos/GIF/GIF.gif'

const Contact = ({ contactParams }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    tipoConsulta: '',
    consulta: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (contactParams && contactParams.tipoConsulta) {
      setFormData(prev => ({ ...prev, tipoConsulta: contactParams.tipoConsulta }));
    }

    if (contactParams && contactParams.scrollToForm) {
      const formSection = document.getElementById('contact-form-section');
      const appHeader = document.getElementById('app-header-mobile-fixed'); 

      if (formSection) {
        setTimeout(() => {
          const headerHeight = appHeader ? appHeader.offsetHeight : 0;
          const formSectionTop = formSection.getBoundingClientRect().top + window.pageYOffset;
          const targetScrollPosition = formSectionTop - headerHeight - 16;

          window.scrollTo({
            top: targetScrollPosition,
            behavior: 'smooth'
          });
        }, 150);
      }
    }
  }, [contactParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    setFormSuccess("");
    setFormError("");

    const missingFields = [];
    if (!formData.nombre) missingFields.push('Nombre');
    if (!formData.email) missingFields.push('Email');
    if (!formData.tipoConsulta) missingFields.push('Tipo de consulta');
    if (!formData.consulta) missingFields.push('Mensaje');
    if (missingFields.length > 0) {
      setFormError('Por favor complete los siguientes campos obligatorios: ' + missingFields.join(', ') + '.');
      return;
    }

    setIsLoading(true);
    try {
      const tipoConsulta = formData.tipoConsulta;
      let tipoConsultaCapitalizado = tipoConsulta;
      if (tipoConsulta && typeof tipoConsulta === 'string') {
        const tipo = tipoConsulta.trim().toLowerCase();
        tipoConsultaCapitalizado = tipo.charAt(0).toUpperCase() + tipo.slice(1);
      }
      const dataToSend = { ...formData, tipoConsulta: tipoConsultaCapitalizado };
      await clientAxios.post('/contacto', dataToSend);
      setFormSuccess("¡Consulta enviada correctamente! Nos pondremos en contacto en menos de 48 horas.");
      setFormData({ nombre: '', empresa: '', email: '', telefono: '', tipoConsulta: '', consulta: '' });
    } catch (err) {
      if (err.response?.data?.error) {
        setFormError(err.response.data.error);
      } else if (err.message && err.message.includes('Network Error')) {
        setFormError('No se pudo conectar con el servidor. Intente más tarde.');
      } else {
        setFormError('Error al enviar la consulta.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const contactMethods = [
    {
      title: "Llámanos",
      description: "Lun - Vie: 8:00 - 18:00 hrs",
      icon: Phone,
      details: "+52 (462) 167-1100",
      // action: "tel:+524621671100",
      color: "bg-gradient-to-br from-orange-500 to-orange-600"
    },
    {
      title: "Escríbenos",
      description: "Respuesta en 24 horas",
      icon: Mail,
      details: "gerencia_rh@almacenesvaca.com.mx",
      action: "mailto:gerencia_rh@almacenesvaca.com.mx",
      color: "bg-gradient-to-br from-green-500 to-green-600"
    },
    {
      title: "Visítanos",
      description: "Irapuato, Guanajuato",
      icon: MapPin,
      details: "Av. San Miguel de Allende 827-A",
      action: "https://maps.google.com",
      color: "bg-gradient-to-br from-blue-500 to-blue-600"
    }
  ];

  const locations = [
    {
      title: "MATRIZ",
      address: "Av. San Miguel de Allende 827-A",
      city: "Irapuato, Guanajuato",
      cp: "CP. 36541",
      phone: "01 (462) 1671100",
      email: "contacto@almacenesvaca.com"
    },
    {
      title: "SUCURSAL GDL",
      address: "Heroes de Nacozari #354-8",
      city: "Guadalajara, Jalisco",
      cp: "CP 44440",
      phone: "01 (33) 36970440",
      email: "contactogdl@almacenesvaca.com"
    },
    {
      title: "SUCURSAL MTY",
      address: "Calle C #523-2",
      city: "Apodaca, Nuevo León",
      cp: "Cd. Industrial Almacentro",
      phone: "01 (81) 21870960",
      email: "contactomty@almacenesvaca.com"
    },
    {
      title: "SUCURSAL VER",
      address: "Av. 11 entre calle 21 y 23 #2104",
      city: "Córdoba, Veracruz",
      cp: "CP. 94650",
      phone: "01 (271) 7123290",
      email: null
    }
  ];

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section - Nuevo diseño */}
      <div className="relative bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="relative max-w-7xl mx-auto py-20 px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center p-2 bg-white bg-opacity-20 rounded-full mb-6">
            <MessageCircle className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6">
            Estamos aquí para ayudarte
          </h1>
          <p className="mt-4 max-w-3xl mx-auto text-xl text-orange-50 leading-relaxed">
            En Almacenes Vaca valoramos cada conversación. Nuestro equipo está listo para
            responder tus preguntas y brindarte la mejor atención.
          </p>
          
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:reclutamiento@almacenesvaca.com.mx"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-xl text-orange-600 bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <Mail className="h-5 w-5 mr-2" />
              Enviar correo
            </a>
            <a
              // href="tel:+5214626218268"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-xl text-white bg-orange-700 hover:bg-orange-800 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <Phone className="h-5 w-5 mr-2" />
              Llamar ahora
            </a>
          </div>
        </div>
      </div>

      {/* Contact Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {contactMethods.map((method, index) => (
            <a
              key={index}
              href={method.action}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-2"
            >
              <div className={`${method.color} p-6 text-white`}>
                <method.icon className="h-10 w-10 mb-4" />
                <h3 className="text-xl font-bold mb-2">{method.title}</h3>
                <p className="text-white text-opacity-90 text-sm flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  {method.description}
                </p>
              </div>
              <div className="p-6 bg-white">
                <p className="text-gray-700 font-medium group-hover:text-orange-600 transition-colors">
                  {method.details}
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Form Section - Nuevo layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          {/* Left info panel */}
          <div className="lg:col-span-2">
            <div className="sticky top-8">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                ¿Tienes alguna pregunta?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Completa el formulario y nos pondremos en contacto contigo en menos de 48 horas.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-xl">
                  <div className="flex-shrink-0 bg-orange-100 rounded-lg p-2">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Tiempo de respuesta</h4>
                    <p className="text-sm text-gray-600">Menos de 48 horas hábiles</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-xl">
                  <div className="flex-shrink-0 bg-green-100 rounded-lg p-2">
                    <Building className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Atención personalizada</h4>
                    <p className="text-sm text-gray-600">Equipo dedicado a tu consulta</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div id="contact-form-section" className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-semibold text-gray-700 mb-2">
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      id="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      required
                      className="w-full border-2 border-gray-200 rounded-xl py-3 px-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      placeholder="Juan Pérez"
                    />
                  </div>

                  <div>
                    <label htmlFor="empresa" className="block text-sm font-semibold text-gray-700 mb-2">
                      Empresa u organización
                    </label>
                    <input
                      type="text"
                      name="empresa"
                      id="empresa"
                      value={formData.empresa}
                      onChange={handleInputChange}
                      className="w-full border-2 border-gray-200 rounded-xl py-3 px-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      placeholder="Mi Empresa S.A."
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email de contacto *
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full border-2 border-gray-200 rounded-xl py-3 px-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      placeholder="correo@ejemplo.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="telefono" className="block text-sm font-semibold text-gray-700 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      id="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      className="w-full border-2 border-gray-200 rounded-xl py-3 px-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      placeholder="(462) 123 4567"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="tipoConsulta" className="block text-sm font-semibold text-gray-700 mb-2">
                    Tipo de consulta *
                  </label>
                  <select
                    id="tipoConsulta"
                    name="tipoConsulta"
                    value={formData.tipoConsulta}
                    onChange={handleInputChange}
                    required
                    className="w-full border-2 border-gray-200 rounded-xl py-3 px-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  >
                    <option value="">Seleccione una opción</option>
                    <option value="Compras">Compras</option>
                    <option value="Reclutamiento">Reclutamiento</option>
                    <option value="Ventas">Ventas</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="consulta" className="block text-sm font-semibold text-gray-700 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="consulta"
                    name="consulta"
                    rows={5} 
                    value={formData.consulta}
                    onChange={handleInputChange}
                    required
                    className="w-full border-2 border-gray-200 rounded-xl py-3 px-4 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
                    placeholder="Cuéntanos sobre tu proyecto, volúmenes requeridos o cualquier detalle relevante..."
                  ></textarea>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="w-full flex justify-center items-center py-4 px-6 rounded-xl shadow-lg text-lg font-semibold text-white bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    disabled={isLoading}
                  >
                    <Send className="h-5 w-5 mr-2" />
                    {isLoading ? 'Enviando...' : 'Enviar mensaje'}
                  </button>
                  
                  {isLoading && (
                    <div className="flex justify-center mt-6">
                      <img src={loadingGIF} alt="Cargando..." className="w-20 h-20" />
                    </div>
                  )}
                  
                  {formSuccess && (
                    <div className="mt-4 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                      <p className="text-green-700 font-medium">{formSuccess}</p>
                    </div>
                  )}
                  
                  {formError && (
                    <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                      <p className="text-red-700 font-medium">{formError}</p>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section - Rediseñado */}
      <div className="bg-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Encuéntranos aquí</h2>
            <p className="text-xl text-gray-600">Nuestra ubicación principal en Irapuato, Guanajuato</p>
          </div>
          
          <div className="rounded-3xl overflow-hidden shadow-2xl border-8 border-white">
            <div style={{ position: 'relative', width: '100%', paddingBottom: '50%' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4676.897026081607!2d-101.2936369!3d20.6394928!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x842c8126256d1e57%3A0x95a74545fca78e38!2sAlmacenes%20Vaca!5e1!3m2!1ses-419!2smx!4v1754696799762!5m2!1ses-419!2smx" 
                width="100%"
                height="450"
                style={{ border: 0, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                allowFullScreen
                loading="lazy"
                title="Almacenes Vaca Ubicación"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Locations Grid - Nuevo diseño */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Nuestras ubicaciones</h2>
          <p className="text-xl text-gray-600">Estamos presentes en varios estados para servirte mejor</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {locations.map((location, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 transform hover:-translate-y-1">
              <div className="bg-gradient-to-r from-green-600 to-green-500 p-4">
                <h3 className="text-white font-bold text-lg">{location.title}</h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex items-start space-x-2">
                  <MapPin className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-700">
                    <p>{location.address}</p>
                    <p>{location.city}</p>
                    <p className="text-gray-500">{location.cp}</p>
                  </div>
                </div>
                
                {location.phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="h-5 w-5 text-orange-600 flex-shrink-0" />
                    <a href={`tel:${location.phone}`} className="text-sm text-gray-700 hover:text-orange-600">
                      {location.phone}
                    </a>
                  </div>
                )}
                
                {location.email && (
                  <div className="flex items-center space-x-2">
                    <Mail className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <a href={`mailto:${location.email}`} className="text-sm text-blue-600 hover:text-blue-800 break-all">
                      {location.email}
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Campo Fresco */}
        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-8 border-2 border-green-200">
          <div className="flex items-center mb-4">
            <Building className="h-8 w-8 text-green-700 mr-3" />
            <h3 className="text-2xl font-bold text-green-900">PLANTA CAMPO FRESCO</h3>
          </div>
          <p className="text-gray-700 text-lg">Carr. Irapuato - Silao km 12357, La Calera</p>
        </div>
      </div>
    </div>
  );
};

export default Contact;